import { useState } from "react";
import { motion } from "framer-motion";
import {
  Utensils, GraduationCap, BookOpen, Compass, Building2,
  Users, ShieldAlert, TrendingUp, ShoppingBag, Check, X,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SERVICES } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

const ICONS = {
  "online-restaurant": Utensils,
  "online-coaching": GraduationCap,
  "book-publishing": BookOpen,
  "tours-travels": Compass,
  "contract-construction": Building2,
  "manpower-supply": Users,
  "fire-fighting": ShieldAlert,
  "trading-wholesale-retail": TrendingUp,
  ecommerce: ShoppingBag,
};

export const Services = ({ onEnquire }) => {
  const { t } = useLanguage();
  const [active, setActive] = useState(null);
  const activeItem = active ? t.serviceItems[active.id] : null;

  return (
    <section id="services" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
      <div className="max-w-2xl">
        <p className="text-xs font-bold uppercase tracking-widest text-rose-600">
          {t.brandFull}
        </p>
        <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
          {t.services.heading}
        </h2>
        <p className="mt-4 text-base text-muted-foreground sm:text-lg">
          {t.services.subheading}
        </p>
      </div>

      <div data-testid="services-grid-container" className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => {
          const item = t.serviceItems[s.id];
          const Icon = ICONS[s.id];
          return (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
              data-testid={`service-card-${s.id}`}
              className="group cursor-pointer overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-shadow hover:shadow-xl"
              onClick={() => setActive(s)}
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={s.image}
                  alt={item.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 flex h-10 w-10 items-center justify-center rounded-xl bg-rose-600 text-white shadow-lg">
                  <Icon className="h-5 w-5" />
                </span>
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-semibold leading-snug">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.desc}</p>
                <div className="mt-4 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`service-details-button-${s.id}`}
                    onClick={(e) => { e.stopPropagation(); setActive(s); }}
                  >
                    {t.services.details}
                  </Button>
                  <Button
                    size="sm"
                    data-testid={`service-enquire-button-${s.id}`}
                    onClick={(e) => { e.stopPropagation(); onEnquire(s.id); }}
                  >
                    {t.services.enquire}
                  </Button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="service-details-modal" className="max-w-lg">
          {active && activeItem && (
            <>
              <div className="relative -mx-6 -mt-6 h-48 overflow-hidden rounded-t-lg">
                <img src={active.image} alt={activeItem.title} className="h-full w-full object-cover" />
              </div>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">{activeItem.title}</DialogTitle>
                <DialogDescription className="text-sm leading-relaxed">
                  {activeItem.long}
                </DialogDescription>
              </DialogHeader>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-rose-600">
                  {t.services.featuresLabel}
                </p>
                <ul className="mt-3 space-y-2">
                  {activeItem.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  data-testid="service-modal-enquire-button"
                  onClick={() => { setActive(null); onEnquire(active.id); }}
                >
                  {t.services.enquire}
                </Button>
                <Button
                  variant="ghost"
                  data-testid="service-modal-close-button"
                  onClick={() => setActive(null)}
                  className="gap-1"
                >
                  <X className="h-4 w-4" /> {t.services.close}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
